import dayjs from '@sancare/typescript-commons/src/misc/dayjs'
import { formatDrugEvent } from '@sancare/typescript-commons/src/misc/DrugEventHelper'
import { Dayjs } from 'dayjs'

import {
  BaseAct,
  BaseCodedAct,
  CodedMedicalAct,
  MedicalAct,
  RawCodedMedicalAct
} from '@/store/modules/medical-act/types'
import { Patient, RawPatient } from '@/store/modules/patient/types'
import { RawStay, Stay } from '@/store/modules/stay/types'

export function formatBaseStay(rawStay: RawStay): Stay {
  return {
    id: rawStay.id,
    administrativeLocalStayId: rawStay.administrativeLocalStayId,
    comment: rawStay.comment,
    drugEvents: (rawStay.drugEvents ?? []).map((event) => formatDrugEvent(event)),
    patient: formatPatient(rawStay.patient),
    patientAge: rawStay.patientAge,
    status: rawStay.status,
    stayDuration: rawStay.stayDuration,
    stayEnd: dayjs(rawStay.stayEnd),
    stayStart: dayjs(rawStay.stayStart),
    lastPredictionUpdate: rawStay.lastPredictionUpdate ? dayjs(rawStay.lastPredictionUpdate) : null,
    lastHealthDataUpdate: rawStay.lastHealthDataUpdate ? dayjs(rawStay.lastHealthDataUpdate) : null,
    lastExternalPmsiUpdate: rawStay.lastExternalPmsiUpdate ? dayjs(rawStay.lastExternalPmsiUpdate) : null,
    annotations: rawStay.annotations,
    reports: (rawStay.reports ?? []).map((report) => ({ ...report, creationDate: report.creationDate ? dayjs(report.creationDate) : null })),
    textualHealthEntries: (rawStay.textualHealthEntries ?? []).map((entry) => ({ ...entry, creationDate: entry.creationDate ? dayjs(entry.creationDate) : null })),
    categoricalLabResults: (rawStay.categoricalLabResults ?? []).map((res) => ({ ...res, creationDate: res.creationDate ? dayjs(res.creationDate) : null })),
    labResults: (rawStay.labResults ?? []).map((res) => ({ ...res, creationDate: res.creationDate ? dayjs(res.creationDate) : null })),
    healthConstants: (rawStay.healthConstants ?? []).map((cst) => ({ ...cst, creationDate: cst.creationDate ? dayjs(cst.creationDate) : null })),
    suggestionJustifications: rawStay.suggestionJustifications,
  }
}

function formatPatient(rawPatient: RawPatient): Patient {
  return {
    id: rawPatient.id,
    ipp: rawPatient.ipp,
    firstName: rawPatient.firstName,
    lastName: rawPatient.lastName,
    maidenName: rawPatient.maidenName,
    gender: rawPatient.gender,
    birthDate: dayjs(rawPatient.birthDate)
  }
}

export function formatCodedMedicalActs(rawActs: RawCodedMedicalAct[]): CodedMedicalAct[] {
  return rawActs.map((rawAct) => formatCodedMedicalAct(rawAct))
}

function formatCodedMedicalAct(rawAct: RawCodedMedicalAct): CodedMedicalAct {
  return {
    ...formatCodedAct<MedicalAct, RawCodedMedicalAct>(rawAct),
    extension: rawAct.extension,
    realisationCount: rawAct.realisationCount,
  }
}

function formatCodedAct
<TAct extends BaseAct, TRawCodedAct extends BaseCodedAct<string, TAct>>(rawAct: TRawCodedAct):
BaseCodedAct<Dayjs, TAct> {
  return {
    id: rawAct.id,
    associatedAct: rawAct.associatedAct,
    disabled: rawAct.disabled,
    executionDate: dayjs(rawAct.executionDate),
    practitioner: rawAct.practitioner
  }
}
